import { useEffect, useState } from 'react';
import { useUser } from '../contexts/userContext';
import api from '../api/client';

const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;

  const apiUrl =
    process.env.REACT_APP_API_URL || `${window.location.origin}/api/`;

  return apiUrl.replace(/\/api\/?$/, '') + '/' + url.replace(/^\//, '');
};

export default function useCompany() {
  const { userDetails } = useUser();

  const [company, setCompany] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handleUpdate = () => setRefreshTrigger(prev => prev + 1);
    window.addEventListener('company-data-updated', handleUpdate);
    return () => window.removeEventListener('company-data-updated', handleUpdate);
  }, []);

  useEffect(() => {
    if (!userDetails) return;

    const fetchCompany = async () => {
      setLoading(true);
      setError(null);

      try {
        let companyId = null;

        // 1️⃣ From user admin_companies
        if (userDetails.admin_companies?.length) {
          try {
            const res = await api.get('/companies/');
            const companies = res.data?.results || res.data || [];

            const match = companies.find(c =>
              userDetails.admin_companies.includes(c.name)
            );

            if (match?.id) {
              companyId = match.id;
            }
          } catch {
            // fallback continues
          }
        }

        // 2️⃣ From token fallback
        if (!companyId) {
          try {
            const token =
              localStorage.getItem('adminToken') ||
              localStorage.getItem('token');

            if (token) {
              const payload = JSON.parse(atob(token.split('.')[1]));
              companyId = payload.company_id;
            }
          } catch {
            // ignore
          }
        }

        if (!companyId) {
          setCompany(null);
          setCompanyLogo(null);
          return;
        }

        // 3️⃣ Fetch company details
        const companyRes = await api.get(`/companies/${companyId}/`);
        const data = companyRes.data;

        setCompany({ ...data, id: data.id ?? companyId });
        setCompanyLogo(resolveMediaUrl(data.logo));
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [userDetails, refreshTrigger]);

  const companyId = company?.id ?? null;

  return {
    company,
    companyId,
    companyLogo,
    loading,
    error,
  };
}
